//https://chatgpt.com/share/6a799b24-19e8-83ea-ac4a-fe6a7a3f2e7e
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, Wallet, Fingerprint, CalendarCheck, BarChart3, CalendarDays,
  MessageSquare, AlertTriangle, FileText, GitBranch, Calculator, ShieldCheck,
  ArrowRight, Mail, Globe, CheckCircle2, Sparkles, Clock, MapPin, Bell,
  Download, Languages, KeyRound, Lock
} from "lucide-react";
import { sessionManager } from "../../utils/sessionManager";
import DemoRequestModal from "./DemoRequestModal";
import "./LandingPage.css";

import logoDark from "../../Images/English Nexploy V2.png";
import logoWhite from "../../Images/English Nexploy white V2.png";
import shotEmployees from "./assets/employees.png";
import shotPayroll from "./assets/payroll.png";
import shotReports from "./assets/reports.png";
import shotAttendance from "./assets/attendance.png";
import shotApp from "./assets/APP.PNG";
import shotLogin from "./assets/LOGIN.PNG";

const MODULES = [
  { icon: Users, title: "Employee Management", text: "Centralized profiles for the full employee lifecycle." },
  { icon: Wallet, title: "Payroll System", text: "Accurate, automated salary processing." },
  { icon: Fingerprint, title: "Attendance", text: "Tracks every check-in and check-out and calculates hours, lateness and overtime automatically." },
  { icon: CalendarCheck, title: "Leave Management", text: "Requests, approvals and balances made simple." },
  { icon: BarChart3, title: "Reports & Analytics", text: "Actionable insights across your HR data." },
  { icon: CalendarDays, title: "Calendar & Events", text: "Shared schedules, events and reminders." },
  { icon: MessageSquare, title: "Messages & Chat", text: "Secure built-in team communication." },
  { icon: AlertTriangle, title: "Escalations", text: "Track and resolve issues efficiently." },
  { icon: FileText, title: "Employee Documents", text: "Paperless records, letters and certificates." },
  { icon: GitBranch, title: "Employee Changes", text: "Promotions and transfers with clean audit trails." },
  { icon: Calculator, title: "Tax Calculator", text: "Stay compliant with local regulations." },
  { icon: ShieldCheck, title: "Admin Levels & Configuration", text: "Fine-grained roles, permissions and policies." },
];

const SHOWCASE = [
  { img: shotPayroll, tag: "Payroll", title: "Error-free payroll, every cycle", text: "Salaries, allowances and deductions handled automatically — reviewed and approved in minutes." },
  { img: shotReports, tag: "Reports & Analytics", title: "Decisions backed by data", text: "Clear, shareable reports across your entire HR operation." },
];

function dashboardPathForRole(role) {
  switch ((role || "").toLowerCase()) {
    case "admin": return "/dashboard/dashboard";
    case "manager": return "/DashboardManager/dashboard";
    case "user": return "/userprofile";
    case "superadmin": return "/DashboardSuper";
    default: return "/login";
  }
}

export default function LandingPage() {
  const [demoOpen, setDemoOpen] = useState(false);
  const isAuthenticated = sessionManager.isAuthenticated();
  const appPath = isAuthenticated
    ? dashboardPathForRole(sessionManager.getUserRole())
    : "/login";
  const appLabel = isAuthenticated ? "Open Dashboard" : "Sign In";
  const openDemo = () => setDemoOpen(true);

  return (
    <div className="nxl">
      <DemoRequestModal open={demoOpen} onClose={() => setDemoOpen(false)} />

      {/* ===== Nav ===== */}
      <header className="nxl-nav">
        <div className="nxl-container nxl-nav-inner">
          <a href="#top" className="nxl-brand">
            <img src={logoDark} alt="NexPloy" />
          </a>
          <nav className="nxl-nav-links">
            <a href="#products">Products</a>
            <a href="#features">Features</a>
            <a href="#showcase">Platform</a>
            <a href="#mobile">Mobile App</a>
            <a href="#contact">Contact</a>
          </nav>
          <div className="nxl-nav-cta">
            <button className="nxl-btn nxl-btn-ghost" onClick={openDemo}>Request a Demo</button>
            <Link className="nxl-btn nxl-btn-primary" to={appPath}>{appLabel}</Link>
          </div>
        </div>
      </header>

      {/* ===== Hero ===== */}
      <section className="nxl-hero" id="top">
        <div className="nxl-container nxl-hero-inner">
          <div className="nxl-hero-copy">
            <span className="nxl-badge"><Sparkles size={14} /> Next Generation Employees</span>
            <h1>The Complete HR Solution for Modern Teams</h1>
            <p>
              NexPloy brings your entire HR operation into one platform —
              so you can focus on people, not paperwork.
            </p>
            <div className="nxl-hero-actions">
              <button className="nxl-btn nxl-btn-primary nxl-btn-lg" onClick={openDemo}>
                Request a Demo <ArrowRight size={18} />
              </button>
              <Link className="nxl-btn nxl-btn-outline nxl-btn-lg" to={appPath}>{appLabel}</Link>
            </div>
            <ul className="nxl-hero-points">
              <li><CheckCircle2 size={16} /> All-in-one platform</li>
              <li><CheckCircle2 size={16} /> Web &amp; mobile apps</li>
              <li><CheckCircle2 size={16} /> Arabic &amp; English</li>
            </ul>
          </div>
          <div className="nxl-hero-shot">
            <div className="nxl-browser">
              <div className="nxl-browser-bar"><span /><span /><span /></div>
              <img src={shotEmployees} alt="NexPloy HRMS dashboard" />
            </div>
          </div>
        </div>
      </section>

      {/* ===== Products ===== */}
      <section className="nxl-section" id="products">
        <div className="nxl-container">
          <div className="nxl-section-head">
            <h2>Our Products</h2>
            <p>One platform today. A growing suite of workforce tools tomorrow.</p>
          </div>
          <div className="nxl-products">
            <div className="nxl-product-card nxl-product-live">
              <div className="nxl-product-top">
                <h3>NexPloy HRMS</h3>
                <span className="nxl-pill nxl-pill-live">Available now</span>
              </div>
              <p>
                A complete HR management system covering the full employee
                journey — with payroll, attendance, leave, documents,
                analytics and built-in chat.
              </p>
              <div className="nxl-product-tags">
                <span>Payroll</span><span>Attendance</span><span>Leave</span>
                <span>Reports</span><span>Chat</span><span>Documents</span>
              </div>
              <div className="nxl-product-actions">
                <Link className="nxl-btn nxl-btn-primary" to={appPath}>
                  {appLabel} <ArrowRight size={16} />
                </Link>
                <button className="nxl-btn nxl-btn-ghost" onClick={openDemo}>Book a demo</button>
              </div>
            </div>
            <div className="nxl-product-card nxl-product-soon">
              <div className="nxl-product-top">
                <h3>More on the way</h3>
                <span className="nxl-pill">Coming soon</span>
              </div>
              <p>
                We are building the next generation of workforce products.
                Follow us — new tools will appear here first.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Features ===== */}
      <section className="nxl-section nxl-section-alt" id="features">
        <div className="nxl-container">
          <div className="nxl-section-head">
            <h2>Everything HR needs, in one place</h2>
            <p>Twelve integrated modules that share one source of truth.</p>
          </div>
          <div className="nxl-grid">
            {MODULES.map(({ icon: Icon, title, text }) => (
              <div className="nxl-card" key={title}>
                <div className="nxl-card-icon"><Icon size={22} /></div>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Showcase ===== */}
      <section className="nxl-section" id="showcase">
        <div className="nxl-container">
          <div className="nxl-section-head">
            <h2>See the platform in action</h2>
            <p>Real screens from NexPloy HRMS.</p>
          </div>

          {SHOWCASE.map(({ img, tag, title, text }, i) => (
            <div className={`nxl-showcase-row ${i % 2 ? "nxl-reverse" : ""}`} key={tag}>
              <div className="nxl-showcase-copy">
                <span className="nxl-pill nxl-pill-blue">{tag}</span>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
              <div className="nxl-browser nxl-showcase-shot">
                <div className="nxl-browser-bar"><span /><span /><span /></div>
                <img src={img} alt={`NexPloy HRMS — ${tag}`} loading="lazy" />
              </div>
            </div>
          ))}

          {/* Attendance — tracked and calculated automatically */}
          <div className="nxl-showcase-row">
            <div className="nxl-showcase-copy">
              <span className="nxl-pill nxl-pill-blue">Attendance</span>
              <h3>Every check-in, tracked and calculated automatically</h3>
              <p>
                NexPloy logs every check-in and check-out — on-site through
                biometric devices or on the go from the mobile app — then
                works out hours worked, lateness, early leaves, overtime and
                absences for each employee, updated in real time.
              </p>
              <ul className="nxl-feature-list">
                <li><Clock size={16} /> Automatic hours &amp; overtime calculation</li>
                <li><AlertTriangle size={16} /> Late, early-leave and absence tracking</li>
                <li><MapPin size={16} /> Biometric and GPS-based mobile check-in</li>
                <li><BarChart3 size={16} /> Manager dashboards and exportable reports</li>
              </ul>
            </div>
            <div className="nxl-browser nxl-showcase-shot">
              <div className="nxl-browser-bar"><span /><span /><span /></div>
              <img src={shotAttendance} alt="NexPloy HRMS — Attendance" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* ===== Mobile App ===== */}
      <section className="nxl-section nxl-section-alt" id="mobile">
        <div className="nxl-container">
          <div className="nxl-showcase-row nxl-reverse">
            <div className="nxl-phone-mock">
              <div className="nxl-phone">
                <div className="nxl-phone-notch" />
                <div className="nxl-phone-screen">
                  <img src={shotApp} alt="NexPloy mobile app" />
                </div>
              </div>
            </div>
            <div className="nxl-showcase-copy">
              <span className="nxl-pill nxl-pill-blue">Mobile App</span>
              <h3>NexPloy in every employee's pocket</h3>
              <p>
                The NexPloy mobile app puts HR in reach wherever your team
                works — clock in with a tap, check payslips and leave
                balances, chat with colleagues, and get notified the moment
                something needs approval.
              </p>
              <ul className="nxl-feature-list">
                <li><Fingerprint size={16} /> One-tap check-in and check-out</li>
                <li><Wallet size={16} /> Payslips and leave balance on hand</li>
                <li><MessageSquare size={16} /> Built-in team chat and alerts</li>
                <li><Bell size={16} /> Real-time push notifications</li>
              </ul>
              <div className="nxl-store-badges">
                <a href="#" className="nxl-store-badge">
                  <Download size={18} />
                  <span><small>Get it on</small>Google Play</span>
                </a>
                <a href="#" className="nxl-store-badge">
                  <Download size={18} />
                  <span><small>Download on the</small>App Store</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Secure Sign-In ===== */}
      <section className="nxl-section" id="login">
        <div className="nxl-container">
          <div className="nxl-showcase-row">
            <div className="nxl-showcase-copy">
              <span className="nxl-pill nxl-pill-blue">Secure Sign-In</span>
              <h3>One login, the right dashboard for everyone</h3>
              <p>
                A single, secure sign-in screen adapts to each person's
                role — employees, managers, admins and super admins land
                straight on the dashboard built for them, in Arabic or
                English.
              </p>
              <ul className="nxl-feature-list">
                <li><Users size={16} /> Role-based dashboards for every level</li>
                <li><Languages size={16} /> Arabic and English out of the box</li>
                <li><KeyRound size={16} /> Self-service password reset</li>
                <li><Lock size={16} /> Protected sessions and access control</li>
              </ul>
            </div>
            <div className="nxl-browser nxl-showcase-shot">
              <div className="nxl-browser-bar"><span /><span /><span /></div>
              <img src={shotLogin} alt="NexPloy HRMS — Login" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="nxl-cta" id="contact">
        <div className="nxl-container nxl-cta-inner">
          <h2>Ready to transform your HR operations?</h2>
          <p>Contact us for a personalized demo of NexPloy HRMS.</p>
          <div className="nxl-hero-actions nxl-center">
            <button className="nxl-btn nxl-btn-light nxl-btn-lg" onClick={openDemo}>
              <Mail size={18} /> Request a Demo
            </button>
            <Link className="nxl-btn nxl-btn-outline-light nxl-btn-lg" to={appPath}>{appLabel}</Link>
          </div>
        </div>
      </section>

      {/* ===== Footer ===== */}
      <footer className="nxl-footer">
        <div className="nxl-container nxl-footer-inner">
          <img src={logoWhite} alt="NexPloy" className="nxl-footer-logo" />
          <div className="nxl-footer-links">
            <a href="mailto:support@nexploy-eg.com"><Mail size={15} /> support@nexploy-eg.com</a>
            <a href="https://nexploy-eg.com"><Globe size={15} /> www.nexploy-eg.com</a>
          </div>
          <p className="nxl-footer-copy">
            © {new Date().getFullYear()} NexPloy — Next Generation Employees. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}